/* @flow */
// Music zones screens (preview, behind ?zones=1):
//  - picker: "Which area is this device playing in?" once per device, after sign-in
//  - manage: the manager's zones list, channel and ads per zone, price
//  - setup:  "Set up a device" QR code and one-time link for staff, when no
//            manager is there to sign in (no password needed)
import React, { Component } from 'react'
import { observer } from 'mobx-react'
import { Modal, ModalBody } from 'reactstrap'
import zones, { ZONE_CHANNELS, isMusicOnly, channelName, MANAGING } from '../../stores/Zones'
import QRCode from '../../vendor/QRCode'
import QRErrorCorrectLevel from '../../vendor/QRCode/QRErrorCorrectLevel'
import { Close } from '../Icons'
import './Zones.css'

// QR code drawn as one SVG path (black squares on white, 4-square quiet zone)
const QR = ({ text, size = 200 }) => {
  const qr = new QRCode(-1, QRErrorCorrectLevel.M)
  qr.addData(text)
  qr.make()
  const n = qr.getModuleCount()
  const pad = 4
  let d = ''
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) d += `M${c + pad},${r + pad}h1v1h-1z`
    }
  }
  const full = n + pad * 2
  return (
    <svg className="zones__qr" width={size} height={size} viewBox={`0 0 ${full} ${full}`} shapeRendering="crispEdges" role="img" aria-label="QR code for the setup link">
      <rect width={full} height={full} fill="#fff" />
      <path d={d} fill="#000" />
    </svg>
  )
}

const Picker = observer(({ onChoose }) => (
  <div className="zones">
    <h2 className="zones__title">Which area is this device playing in?</h2>
    <p className="zones__lead">You only need to do this once on each device. You can change it later from your account menu.</p>
    <div className="zones__pick-list">
      {zones.zones.map(z => (
        <button key={z.id} type="button" className="zones__pick" onClick={() => onChoose(z)}>
          <span className="zones__pick-name">{z.name}</span>
          <span className="zones__pick-channel">
            {channelName(z.channel)}{isMusicOnly(z.channel) ? ' · music only' : ''}
          </span>
        </button>
      ))}
      <button type="button" className="zones__pick zones__pick--quiet" onClick={() => zones.chooseZone(MANAGING)}>
        <span className="zones__pick-name">Just managing, don't play here</span>
        <span className="zones__pick-channel">For your own phone or laptop</span>
      </button>
    </div>
    <div className="zones__actions">
      <button type="button" className="zones__link" onClick={() => zones.open('manage')}>Manage zones</button>
    </div>
  </div>
))

const ZoneRow = observer(({ row, index, onPlayHere }) => {
  const z = row.zone
  const musicOnly = isMusicOnly(z.channel)
  const here = zones.deviceZoneId === z.id
  return (
    <div className={'zones__row' + (here ? ' is-here' : '')}>
      <div className="zones__row-top">
        <input
          className="zones__name"
          value={z.name}
          aria-label="Zone name"
          onChange={e => zones.updateZone(z.id, { name: e.target.value })}
        />
      </div>
      <div className="zones__row-fields">
        <label className="zones__field">
          <span>Channel</span>
          <select value={z.channel} onChange={e => zones.updateZone(z.id, { channel: e.target.value })}>
            {ZONE_CHANNELS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className={'zones__field zones__field--toggle' + (musicOnly ? ' is-disabled' : '')}>
          <input
            type="checkbox"
            checked={!musicOnly && z.ads}
            disabled={musicOnly}
            onChange={e => zones.updateZone(z.id, { ads: e.target.checked })}
          />
          <span>{musicOnly ? 'Music only (no ads or announcements)' : 'Ads & announcements'}</span>
        </label>
      </div>
      <div className="zones__row-bottom">
        <button type="button" className="zones__mini zones__mini--pink" onClick={() => zones.makeSetupLink(z.id)}>Set up a device</button>
        {here
          ? <span className="zones__here">Playing on this device</span>
          : <button type="button" className="zones__mini" onClick={() => onPlayHere(z)}>Play here</button>}
        {zones.zones.length > 1 && index > 0 && (
          <button type="button" className="zones__mini zones__mini--danger" onClick={() => zones.removeZone(z.id)}>Remove</button>
        )}
      </div>
    </div>
  )
})

const Manage = observer(({ onChoose }) => (
  <div className="zones">
    <h2 className="zones__title">Music zones</h2>
    <p className="zones__lead">Play a different channel in each area.</p>
    <div className="zones__list">
      {zones.priced.map((row, i) => (
        <ZoneRow key={row.zone.id} row={row} index={i} onPlayHere={onChoose} />
      ))}
    </div>
    <button type="button" className="zones__add" onClick={() => zones.addZone()}>+ Add a zone</button>
    {zones.isManaging && <p className="zones__note">This device is set to manage only, so it doesn't play music.</p>}
    {zones.deviceZone && (
      <div className="zones__device">
        <div>
          <strong>Staff can reach this device?</strong>
          <span>Make it play-only: it keeps playing {zones.deviceZone.name}, but Ad Studio, zones and billing need the manager to sign in again.</span>
        </div>
        <button type="button" className="zones__mini" onClick={() => zones.open('playOnly')}>Make this device play-only</button>
      </div>
    )}
    <p className="zones__note">Preview: zones are saved in this browser only, and billing doesn't change yet.</p>
  </div>
))

class Setup extends Component<{}, { copied: boolean }> {
  state = { copied: false }

  copy = () => {
    const done = () => { this.setState({ copied: true }); setTimeout(() => this.setState({ copied: false }), 2000) }
    try {
      window.navigator.clipboard.writeText(zones.setupUrl).then(done, () => {})
    } catch (e) {}
  }

  render () {
    const zone = zones.setupZone
    if (!zone) return null
    const url = zones.setupUrl
    const subject = encodeURIComponent(`Set up GymRadio.com music for ${zone.name}`)
    const body = encodeURIComponent(`Open this link on the device that plays music in ${zone.name}. It sets the device up straight away, with no password needed. The link works once and stops working after 24 hours.\n\n${url}`)
    const sms = encodeURIComponent(`Set up GymRadio.com music for ${zone.name}: open this link on that device (works once, for 24 hours) ${url}`)
    return (
      <div className="zones">
        <h2 className="zones__title">Set up a device for {zone.name}</h2>
        <p className="zones__lead">Scan the code with the device's camera, or send the link to staff to open on it. Music starts straight away, with no password needed.</p>
        <div className="zones__setup">
          <QR text={url} />
          <div className="zones__setup-side">
            <div className="zones__setup-link">{url.replace(/^https?:\/\//, '')}</div>
            <div className="zones__setup-buttons">
              <button type="button" className="zones__mini" onClick={this.copy}>{this.state.copied ? 'Copied' : 'Copy link'}</button>
              <a className="zones__mini" href={`mailto:?subject=${subject}&body=${body}`}>Email link</a>
              <a className="zones__mini" href={`sms:?&body=${sms}`}>Text link</a>
            </div>
            <ul className="zones__setup-facts">
              <li>Works once, for 24 hours</li>
              <li>The device can only play {zone.name}: no billing or other zones</li>
              <li>A device you no longer trust can be removed from the zone</li>
            </ul>
          </div>
        </div>
        <div className="zones__actions">
          <button type="button" className="zones__link" onClick={() => zones.open('manage')}>Back to zones</button>
        </div>
        <p className="zones__note">Preview: the link only works in this browser for now.</p>
      </div>
    )
  }
}

const Speaker = observer(() => (
  <div className="zones">
    <h2 className="zones__title">This will play through the {zones.deviceZone ? zones.deviceZone.name : ''} speakers</h2>
    <p className="zones__lead">This device plays music in {zones.deviceZone ? zones.deviceZone.name : 'a zone'}, so members will hear the preview and the music pauses while it plays.</p>
    <p className="zones__lead">To listen privately, open Ad Studio on your phone or laptop and choose "Just managing, don't play here".</p>
    <div className="zones__actions">
      <button type="button" className="zones__primary" onClick={() => zones.speakerAnswer(true)}>Play here anyway</button>
      <button type="button" className="zones__link" onClick={() => zones.speakerAnswer(false)}>Cancel</button>
    </div>
  </div>
))

const PlayOnlyConfirm = observer(() => {
  const zone = zones.deviceZone
  if (!zone) return null
  return (
    <div className="zones">
      <h2 className="zones__title">Make this device play-only?</h2>
      <p className="zones__lead">This device will keep playing {zone.name}, but nobody using it will be able to:</p>
      <ul className="zones__warn-list">
        <li>open Ad Studio, or change ads and class announcements</li>
        <li>change channels, zones or the explicit filter</li>
        <li>see billing or your account</li>
      </ul>
      <div className="zones__warning">
        <strong>To undo this, the manager has to sign in again on this device.</strong> Make sure you know your GymRadio.com email and password before you go ahead.
      </div>
      <div className="zones__actions">
        <button type="button" className="zones__primary" onClick={() => zones.makePlayOnly()}>Make play-only</button>
        <button type="button" className="zones__link" onClick={() => zones.open('manage')}>Cancel</button>
      </div>
    </div>
  )
})

const LinkError = observer(() => (
  <div className="zones">
    <h2 className="zones__title">Setup link not valid</h2>
    <p className="zones__lead">{zones.linkError}</p>
  </div>
))

class Zones extends Component<{ isAuthenticated: boolean, onZoneChannel: Function }> {
  componentDidMount () {
    if (zones.justSetUp && this.props.onZoneChannel) {
      this.props.onZoneChannel(zones.justSetUp.channel, zones.justSetUp)
      zones.justSetUp = null
    }
    zones.askIfNeeded(this.props.isAuthenticated)
  }

  componentDidUpdate () { zones.askIfNeeded(this.props.isAuthenticated) }

  choose = (zone) => {
    zones.chooseZone(zone.id)
    if (this.props.onZoneChannel) this.props.onZoneChannel(zone.channel, zone)
  }

  render () {
    if (!zones.enabled) return null
    const { screen } = zones
    // The first-time picker can't be dismissed until an area is chosen
    const mustChoose = screen === 'picker' && !zones.deviceZone && !zones.isManaging
    return (
      <Modal
        isOpen={!!screen}
        toggle={mustChoose ? undefined : () => zones.close()}
        centered
        className="zones-modal"
        size={screen === 'manage' || screen === 'setup' ? 'lg' : undefined}
      >
        <ModalBody>
          {!mustChoose && (
            <button type="button" className="zones__close" aria-label="Close" onClick={() => zones.close()}>
              <Close />
            </button>
          )}
          {screen === 'picker' && <Picker onChoose={this.choose} />}
          {screen === 'manage' && <Manage onChoose={this.choose} />}
          {screen === 'setup' && <Setup />}
          {screen === 'linkError' && <LinkError />}
          {screen === 'speaker' && <Speaker />}
          {screen === 'playOnly' && <PlayOnlyConfirm />}
        </ModalBody>
      </Modal>
    )
  }
}

export default observer(Zones)

// Small label for the header: which zone this device plays
export const ZoneBadge = observer(() => {
  if (!zones.enabled || (!zones.deviceZone && !zones.isManaging)) return null
  const name = zones.isManaging ? 'Managing only' : zones.deviceZone.name
  if (zones.playOnly) {
    return (
      <span className="zones-badge is-locked" title="This device is play-only">
        <span className="zones-badge__label">Zone</span>{name}
      </span>
    )
  }
  return (
    <button type="button" className="zones-badge" onClick={() => zones.open('manage')} title="Change zones">
      <span className="zones-badge__label">Zone</span>{name}
    </button>
  )
})
